import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MeetingTopicRequestDto } from 'src/api/FiretrolApiClient';
import { meetingTopicApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useCreateMeetingTopic(jobId: number, meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      meetingTopicApiClient.createTopic(jobId, meetingId, undefined),
    onSettled: () =>
      invalidateFor('meetingTopic.save', qc, { jobId, meetingId }),
  });
}

/**
 * Topic edits autosave, so there is no success message — but a failure has to
 * toast, or the user never learns their notes were dropped. The scope makes
 * TanStack run one write at a time per topic, so two keystroke bursts cannot
 * land out of order.
 */
export function useUpdateMeetingTopic(
  jobId: number,
  meetingId: string,
  topicId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `meeting-topic-${topicId}` },
    mutationFn: (request: MeetingTopicRequestDto) =>
      meetingTopicApiClient.updateTopic(jobId, meetingId, topicId, request),
    meta: { errorContext: 'topic autosave' },
    onSettled: () =>
      invalidateFor('meetingTopic.save', qc, { jobId, meetingId }),
  });
}

export function useDeleteMeetingTopic(jobId: number, meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topicId: string) =>
      meetingTopicApiClient.deleteTopic(jobId, meetingId, topicId),
    onSettled: () =>
      invalidateFor('meetingTopic.save', qc, { jobId, meetingId }),
  });
}
